import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Pencil, CalendarClock, MapPin, User } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const CATEGORIES = ["Class", "Spa Treatment", "Wellness", "Dining", "Activity", "Kids Club"];
const empty = { title: "", category: "Class", day: "Monday", start_time: "", end_time: "", instructor: "", location: "", capacity: "", description: "" };

export default function SchedulePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/schedule").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it, capacity: it.capacity ?? "" }); setOpen(true); };

  const save = async () => {
    if (!form.title) return toast.error("Title is required");
    const payload = { ...form, capacity: form.capacity === "" ? null : parseInt(form.capacity, 10) };
    try {
      if (editing) await api.put(`/schedule/${editing.id}`, payload);
      else await api.post("/schedule", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch { toast.error("Could not save"); }
  };

  const del = async (id) => { await api.delete(`/schedule/${id}`); toast.success("Removed"); load(); };

  const byDay = DAYS.map((d) => ({ day: d, events: items.filter((e) => e.day === d).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")) })).filter((g) => g.events.length);

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl text-foreground mb-1">Wellness & Class Schedule</h1>
          <p className="text-muted-foreground text-sm">Live timetable of classes, spa slots and activities guests can book by time.</p>
        </div>
        <Button onClick={openNew} data-testid="add-schedule-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-2" /> Add slot</Button>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-accent" /> : items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-md">
          <CalendarClock className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground text-sm">No scheduled slots yet.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {byDay.map((g) => (
            <div key={g.day}>
              <h3 className="font-serif text-lg text-foreground mb-3">{g.day}</h3>
              <div className="bg-card border border-border rounded-md divide-y divide-border">
                {g.events.map((e) => (
                  <div key={e.id} className="p-4 flex items-center justify-between gap-4" data-testid={`schedule-item-${e.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="text-center w-20 shrink-0">
                        <p className="font-serif text-foreground">{e.start_time || "—"}</p>
                        {e.end_time && <p className="text-xs text-muted-foreground">to {e.end_time}</p>}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-foreground">{e.title}</p>
                          <Badge variant="secondary" className="text-xs">{e.category}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3">
                          {e.instructor && <span className="flex items-center gap-1"><User className="w-3 h-3" />{e.instructor}</span>}
                          {e.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location}</span>}
                          {e.capacity != null && <span>{e.capacity} spaces</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => openEdit(e)} data-testid={`edit-schedule-${e.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="outline" onClick={() => del(e.id)} data-testid={`delete-schedule-${e.id}`} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing ? "Edit slot" : "Add schedule slot"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input data-testid="schedule-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="Sunrise Yoga" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="schedule-category" className="mt-1.5 w-full h-10 rounded-md border border-input px-3 text-sm bg-background">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label>Day</Label>
                <select value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} data-testid="schedule-day" className="mt-1.5 w-full h-10 rounded-md border border-input px-3 text-sm bg-background">
                  {DAYS.map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Start time</Label><Input data-testid="schedule-start" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1.5" placeholder="07:00" /></div>
              <div><Label>End time</Label><Input data-testid="schedule-end" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1.5" placeholder="08:00" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Instructor</Label><Input data-testid="schedule-instructor" value={form.instructor} onChange={(e) => setForm({ ...form, instructor: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Location</Label><Input data-testid="schedule-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Capacity (optional)</Label><Input data-testid="schedule-capacity" type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Description</Label><Textarea data-testid="schedule-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1.5" /></div>
            <Button onClick={save} data-testid="save-schedule-btn" className="w-full bg-primary text-primary-foreground">Save slot</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
